// CoalitionTrackingBox.jsx
import { useState, useMemo } from "react";
import { Box, Typography, Chip, ButtonBase } from "@mui/material";
import { motion, LayoutGroup } from "framer-motion";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// ── Party / coalition config ─────────────────────────────────────────
const PARTY_META = {
  rightcoalition: { label: "Right Coalition", color: "#111184" },
  nat:            { label: "National",        color: "#0052cc" },
  act:            { label: "ACT",             color: "#D4A800" },
  nzf:            { label: "NZF",             color: "#222222" },
  leftcoalition:  { label: "Left Coalition",  color: "#8B0000" },
  lab:            { label: "Labour",          color: "#E82C2C" },
  green:          { label: "Green",           color: "#009639" },
  tpm:            { label: "TPM",             color: "#CC0033" },
  top:            { label: "TOP",             color: "#008080" },
};

const PARTY_META_ACTIVE_AT_OPEN = {
  rightcoalition: { label: "Right Coalition", color: "#111184" },
  nat:            { label: "National",        color: "#0052cc" },
  act:            { label: "ACT",             color: "#D4A800" },
  nzf:            { label: "NZF",             color: "#222222" },
  leftcoalition:  { label: "Left Coalition",  color: "#8B0000" },
};

const VOTE_SHARE_FIELDS = {
  nat: "nat_pred_n",
  act: "act_pred_n",
  nzf: "nzf_pred_n",
  lab: "lab_pred_n",
  green: "green_pred_n",
  tpm: "tpm_pred_n",
  top: "top_pred_n",
};

const CONSIDER_FIELDS = {
  rightcoalition: "coalition_consider",
  nat: "nat_consider",
  act: "act_consider",
  nzf: "nzf_consider",
  leftcoalition: "coalition_left_consider",
  lab: "lab_consider",
  green: "green_consider",
  tpm: "tpm_consider",
  top: "top_consider",
};

const ISSUE_DEFS = [
  { key: "inflation_issue_n",        label: "Inflation" },
  { key: "health_issue_n",           label: "Health" },
  { key: "econ_issue_n",             label: "Economy" },
  { key: "crime_issue_n",            label: "Crime" },
  { key: "housing_issue_n",          label: "Housing" },
  { key: "unemployment_issue_n",     label: "Unemployment" },
  { key: "poverty_issue_n",          label: "Poverty" },
  { key: "education_issue_issue_n",  label: "Education" },
  { key: "climate_issue_n",          label: "Climate" },
  { key: "transport_issue_n",        label: "Transport" },
  { key: "tax_issue_n",              label: "Tax" },
  { key: "drugs_issue_n",            label: "Drugs" },
  { key: "race_rel_issue_n",         label: "Race Relations" },
  { key: "immigration_issue_n",      label: "Immigration" },
  { key: "maori_issue_n",            label: "Māori Issues" },
  { key: "pollution_issue_n",        label: "Pollution" },
  { key: "defence_issue_n",          label: "Defence" },
];

const ISSUE_COLOURS = [
  "#D32F2F", "#E64A19", "#F57C00", "#FBC02D", "#9E9D24",
  "#388E3C", "#00897B", "#0288D1", "#1565C0", "#4527A0",
  "#6A1B9A", "#AD1457", "#5D4037", "#455A64", "#78909C",
  "#8D6E63", "#546E7A",
];

// Only 3 tabs now — consideration & vote share are merged
const METRICS = [
  { id: "consider", label: "Consideration & Vote Share" },
  { id: "issues",   label: "Issues" },
  { id: "goodjob",  label: "Net Good Job" },
];

const num = (v) => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

// ── Custom tooltip ───────────────────────────────────────────────────
function TrackingTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  // Group by party: pair up consider vs vote lines
  const considers = payload.filter((p) => !p.dataKey.endsWith(" (vote)"));
  const votes = payload.filter((p) => p.dataKey.endsWith(" (vote)"));

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        px: 1.5,
        py: 1,
        fontSize: 12,
        boxShadow: 2,
        maxHeight: 360,
        overflowY: "auto",
      }}
    >
      <Box sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Box>

      {votes.length > 0
        ? considers
            .sort((a, b) => b.value - a.value)
            .map((c) => {
              const voteEntry = votes.find(
                (v) => v.dataKey === `${c.name} (vote)`
              );
              const gap = voteEntry
                ? (c.value - voteEntry.value).toFixed(1)
                : null;

              return (
                <Box key={c.name} sx={{ py: 0.2 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: c.color,
                        flexShrink: 0,
                      }}
                    />
                    <span>
                      <strong>{c.name}</strong>
                    </span>
                  </Box>
                  <Box sx={{ pl: 2.25, color: "text.secondary" }}>
                    Consider: {c.value.toFixed(1)}%
                    {voteEntry && <> · Vote: {voteEntry.value.toFixed(1)}%</>}
                    {gap && (
                      <span style={{ color: "#888" }}> · Gap: {gap}pp</span>
                    )}
                  </Box>
                </Box>
              );
            })
        : payload
            .sort((a, b) => b.value - a.value)
            .map((entry) => (
              <Box
                key={entry.name}
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  py: 0.15,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: entry.color,
                    flexShrink: 0,
                  }}
                />
                <span>
                  {entry.name}: <strong>{entry.value.toFixed(1)}%</strong>
                </span>
              </Box>
            ))}
    </Box>
  );
}

// ── Custom legend for merged chart ───────────────────────────────────
function MergedLegend() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: 2,
        mt: 1,
        fontSize: 11,
        color: "text.secondary",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box sx={{ width: 20, height: 0, borderTop: "2.5px solid #888" }} />
        <span>Consideration</span>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          sx={{
            width: 20,
            height: 0,
            borderTop: "2.5px dashed #888",
          }}
        />
        <span>Vote Share</span>
      </Box>
    </Box>
  );
}

// ── Main component ───────────────────────────────────────────────────
export default function CoalitionTrackingBox({
  metricRowsConst,
  metricRowsNat,
  selectedFeature,
  WIDTH,
}) {
  const [metric, setMetric] = useState("consider");
  const [activeIssues, setActiveIssues] = useState(() =>
    ISSUE_DEFS.slice(0, 6).map((d) => d.key)
  );
  const [activeParties, setActiveParties] = useState(() =>
    Object.keys(PARTY_META_ACTIVE_AT_OPEN)
  );

  // ── Source rows (all waves) ────────────────────────────────────────
  const sourceRows = useMemo(() => {
    if (!metricRowsNat) return null;
    if (selectedFeature && !metricRowsConst) return null;
    if (selectedFeature && metricRowsConst) {
      return metricRowsConst.filter(
        (g) => Number(g.area_id) === Number(selectedFeature.area_id)
      );
    }
    return metricRowsNat;
  }, [metricRowsNat, metricRowsConst, selectedFeature]);

  const waves = useMemo(() => {
    if (!sourceRows) return [];
    return sourceRows
      .slice()
      .sort((a, b) => Number(a.wave) - Number(b.wave));
  }, [sourceRows]);

  // ── Merged consideration + vote share data ─────────────────────────
  const considerVoteData = useMemo(() => {
    return waves.map((r) => {
      const point = { wave: r.wave_date || `Wave ${r.wave}` };

      // Consideration lines (solid)
      Object.entries(CONSIDER_FIELDS).forEach(([party, field]) => {
        point[PARTY_META[party].label] = num(r[field]) / 10;
      });

      // Vote share lines (dashed)
      Object.entries(VOTE_SHARE_FIELDS).forEach(([party, field]) => {
        point[`${PARTY_META[party].label} (vote)`] = num(r[field]);
      });

      // Coalition vote sums
      point[`${PARTY_META.rightcoalition.label} (vote)`] =
        num(r.nat_pred_n) + num(r.act_pred_n) + num(r.nzf_pred_n);
      point[`${PARTY_META.leftcoalition.label} (vote)`] =
        num(r.lab_pred_n) + num(r.green_pred_n) + num(r.tpm_pred_n);

      return point;
    });
  }, [waves]);

  // ── Issue data ─────────────────────────────────────────────────────
  const issueData = useMemo(() => {
    return waves.map((r) => {
      const point = { wave: r.wave_date || `Wave ${r.wave}` };
      ISSUE_DEFS.forEach((d) => {
        point[d.label] = num(r[d.key]);
      });
      return point;
    });
  }, [waves]);

  // ── Good job data ──────────────────────────────────────────────────
  const goodjobData = useMemo(() => {
    return waves.map((r) => ({
      wave: r.wave_date || `Wave ${r.wave}`,
      "Net Good Job": num(r.gov_good_n) - num(r.gov_bad_n),
      "Good Job": num(r.gov_good_n),
      "Bad Job": num(r.gov_bad_n),
    }));
  }, [waves]);

  if (!sourceRows || waves.length === 0) return null;

  // ── Resolve current chart config ───────────────────────────────────
  let chartData, lines, description, customLegend;

  if (metric === "consider") {
    chartData = considerVoteData;
    description =
      "Solid lines show consideration share; dashed lines show actual vote share. The gap reveals each party's conversion potential.";
    customLegend = true;

    lines = Object.entries(PARTY_META)
      .filter(([party]) => activeParties.includes(party))
      .flatMap(([, { label, color }]) => [
        {
          dataKey: label,
          color,
          strokeDasharray: undefined,
        },
        {
          dataKey: `${label} (vote)`,
          color,
          strokeDasharray: "6 3",
          legendHide: true,
        },
      ]);
  } else if (metric === "issues") {
    chartData = issueData;
    description =
      "Share of voters naming each issue a top concern. Toggle issues below.";
    customLegend = false;
    lines = ISSUE_DEFS.filter((d) => activeIssues.includes(d.key)).map(
      (d) => ({
        dataKey: d.label,
        color: ISSUE_COLOURS[ISSUE_DEFS.indexOf(d) % ISSUE_COLOURS.length],
      })
    );
  } else {
    chartData = goodjobData;
    description =
      "Net 'good job' rating of the government (% good minus % bad).";
    customLegend = false;
    lines = [
      { dataKey: "Net Good Job", color: "#1565C0" },
      { dataKey: "Good Job", color: "#388E3C" },
      { dataKey: "Bad Job", color: "#D32F2F" },
    ];
  }

  const toggleIssue = (key) => {
    setActiveIssues((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleParty = (key) => {
    setActiveParties((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  return (
    <Box sx={{ p: 2, border: "1px solid #ddd", borderRadius: 2, width: WIDTH }}>
      <Typography variant="h6" gutterBottom>
        Tracking
      </Typography>

      {/* Metric selector pills */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0,
          p: 0,
          borderRadius: 99,
          bgcolor: "rgba(0,0,0,0.04)",
          mb: 2,
          width: "fit-content",
          mx: "auto",
        }}
      >
        <LayoutGroup id="metric-pills-group">
          {METRICS.map((m) => {
            const isSelected = metric === m.id;

            return (
              <ButtonBase
                key={m.id}
                disableRipple
                onClick={() => setMetric(m.id)}
                sx={{
                  position: "relative",
                  px: 2,
                  py: 0.65,
                  borderRadius: 99,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: isSelected ? "#1976D2" : "#BDBDBD",
                  transition: "color 0.2s ease",
                  "&:hover": {
                    color: isSelected ? "#BDBDBD" : "#1976D2",
                    backgroundColor: !isSelected
                      ? "rgba(0,0,0,0.04)"
                      : "transparent",
                  },
                  zIndex: 1,
                }}
              >
                <span style={{ position: "relative", zIndex: 2 }}>
                  {m.label}
                </span>

                {isSelected && (
                  <Box
                    component={motion.div}
                    layoutId="metric-active-pill"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 99,
                      bgcolor: "white",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                      border: "1px solid rgba(0,0,0,0.02)",
                      zIndex: 1,
                    }}
                  />
                )}
              </ButtonBase>
            );
          })}
        </LayoutGroup>
      </Box>

      {/* Description */}
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        {description}
      </Typography>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis
            dataKey="wave"
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
            width={44}
          />
          <Tooltip content={<TrackingTooltip />} />
          {!customLegend && (
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
            />
          )}
          {lines.map(({ dataKey, color, strokeDasharray, legendHide }) => (
            <Line
              key={dataKey}
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2.5}
              strokeDasharray={strokeDasharray}
              dot={{ r: 4, fill: color, strokeWidth: 0 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
              hide={false}
              legendType={legendHide ? "none" : "circle"}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {/* Custom legend for merged chart: solid = consider, dashed = vote */}
      {customLegend && <MergedLegend />}

      {/* Party toggle chips for merged chart */}
      {metric === "consider" && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 2 }}>
          {Object.entries(PARTY_META).map(([key, { label, color }]) => {
            const active = activeParties.includes(key);

            return (
              <Chip
                key={key}
                label={label}
                size="small"
                onClick={() => toggleParty(key)}
                sx={{
                  fontSize: 11,
                  fontWeight: active ? 600 : 400,
                  cursor: "pointer",
                  bgcolor: active ? color : "transparent",
                  color: active ? "#000000" : "#000000",
                  border: "1px solid",
                  borderColor: active ? color : "divider",
                    "& .MuiChip-label": {
                        color: active ? "#fff" : "#999999",
                    },
                    "&:hover": {
                        bgcolor: active ? color : "action.hover",
                    },
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Issue toggle chips */}
      {metric === "issues" && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 2 }}>
          {ISSUE_DEFS.map((d, i) => {
            const active = activeIssues.includes(d.key);
            const col = ISSUE_COLOURS[i % ISSUE_COLOURS.length];

            return (
              <Chip
                key={d.key}
                label={d.label}
                size="small"
                onClick={() => toggleIssue(d.key)}
                sx={{
                  fontSize: 11,
                  fontWeight: active ? 600 : 400,
                  cursor: "pointer",
                  bgcolor: active ? col : "transparent",
                  color: active ? "#fff" : "text.secondary",
                  border: "1px solid",
                  borderColor: active ? col : "divider",
                    "& .MuiChip-label": {
                        color: active ? "#fff" : "#999999",
                    },
                    "&:hover": {
                        bgcolor: active ? col : "action.hover",
                    },
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}